import React, { useMemo } from 'react';
import { FolderNav as FolderNavComponent } from '@orijinworks/frontend-commons';
import { Box, withStyles } from '@material-ui/core';
import styles from './index.style';
import CheckEvents from '../users/checkEvents';
import { VerifiedUserIcon } from '../../v2/shared/components/MaterialUIIcons';
import {
  EventNote,
  LocationOnOutlined,
  ListAlt,
  PersonOutlined,
  Queue,
  Settings,
  SwapHoriz,
  CardMembership,
  AccountTree,
  Widgets,
} from '@material-ui/icons';
import { ManageAds } from '../../v2/modules/manage-ads';
import LocationConfigManager from './LocationConfigManager';
import ManagePrograms from '../../v2/modules/manage-programs/manage-programs'
import ManageRolesV2 from '../../v2/modules/manage-role/manage-roles'
import { EntertainmentIcon } from '../icons';
import useUnleashHook from '../../v2/core/unleash/useUnleashHook';
import { withUnleash } from '../../v2/core/unleash';
import { UnleashService } from '../../v2/services';
import { ManageLocations } from '../../v2/modules/manage-locations';
import PersonsManager from './PersonsManager';
import ServiceConfiguration from './ServiceConfiguration';
import LmsCourseReview from '../../v2/modules/lms-course-review/lms-course-review';
import FacilityLmsCutover from '../../v2/modules/facility-lms-cutover/facility-lms-cutover';
import ProgramCertificates from '../../v2/modules/program-certificates/program-certificates';
import CapMapping from '../../v2/modules/cap-mapping';
import useSudoPermission from '../../constants/permissions/useSudoPermission';

const LocationManagementTab = () => <ManageLocations />;
const RolesTab = () => <ManageRolesV2 />;
const CheckEventsTab = () => <CheckEvents />;
const LocationConfigManagerTab = () => <LocationConfigManager />;
const PersonConfigurationTab = () => <PersonsManager />;
const ServiceConfigurationTab = () => <ServiceConfiguration />;
const ManageAdsTab = () => <ManageAds />;
const ManageProgramsTab = () => <ManagePrograms />;
const CourseReviewTab = () => <LmsCourseReview />;
const FacilityLmsTab = () => <FacilityLmsCutover />;
const ProgramCertificatesTab = () => <ProgramCertificates />;
const CapMappingTab = () => <CapMapping />;

const SuperAdminModule = ({ classes, unleashProps }) => {
  const hasSudo = useSudoPermission();
  const showServiceConfiguration = hasSudo;

  const adsManagementFlagEnabled = unleashProps.isFlagEnabled();
  const { isFlagEnabled: manageProgramFlagEnabled } = useUnleashHook(UnleashService.FLAGS.MANAGE_PROGRAMS);
  const { isFlagEnabled: courseReviewFlagEnabled } = useUnleashHook(UnleashService.FLAGS.COURSE_REVIEW);
  const { isFlagEnabled: facilityLmsCutoverFlagEnabled } = useUnleashHook(UnleashService.FLAGS.FACILITY_LMS_CUTOVER);
  const { isFlagEnabled: manualProgramCertFlagEnabled } = useUnleashHook(
    UnleashService.FLAGS.MANUAL_PROGRAM_CERT_GENERATION
  );
  const { isFlagEnabled: capMappingManagerFlagEnabled } = useUnleashHook(UnleashService.FLAGS.CAP_MAPPING_MANAGER);

  const showManagePrograms = manageProgramFlagEnabled();
  const showCourseReview = courseReviewFlagEnabled();
  const showFacilityLms = facilityLmsCutoverFlagEnabled();
  const showProgramCertificates = manualProgramCertFlagEnabled();
  const showCapMapping = capMappingManagerFlagEnabled() && hasSudo;

  const tabs = useMemo(() => {
    const nextTabs = [
      {
        label: 'Location Management',
        value: '1',
        component: LocationManagementTab,
        Icon: LocationOnOutlined,
      },
      {
        label: 'Roles',
        value: '2',
        component: RolesTab,
        Icon: VerifiedUserIcon,
      },
      {
        label: 'Check Events',
        value: '3',
        component: CheckEventsTab,
        Icon: EventNote,
      },
      {
        label: 'Configuration Manager',
        value: '6',
        component: LocationConfigManagerTab,
        Icon: Settings,
      },
      {
        label: 'Person Configuration',
        value: '7',
        component: PersonConfigurationTab,
        Icon: PersonOutlined,
      },
    ];

    if (adsManagementFlagEnabled) {
      nextTabs.push({
        label: 'Manage Ads',
        value: '4',
        component: ManageAdsTab,
        Icon: EntertainmentIcon,
      });
    }

    if (showManagePrograms) {
      nextTabs.push({
        label: 'Manage Programs',
        value: '5',
        component: ManageProgramsTab,
        Icon: ListAlt,
      });
    }

    if (showCourseReview) {
      nextTabs.push({
        label: 'Course Review',
        value: '8',
        component: CourseReviewTab,
        Icon: Queue,
      });
    }

    if (showFacilityLms) {
      nextTabs.push({
        label: 'Facility LMS',
        value: '9',
        component: FacilityLmsTab,
        Icon: SwapHoriz,
      });
    }

    if (showProgramCertificates) {
      nextTabs.push({
        label: 'Pending Certificates',
        value: '10',
        component: ProgramCertificatesTab,
        Icon: CardMembership,
      });
    }

    if (showCapMapping) {
      nextTabs.push({
        label: 'CAP Mappings',
        value: '11',
        component: CapMappingTab,
        Icon: AccountTree,
      });
    }

    if (showServiceConfiguration) {
      nextTabs.push({
        label: 'Service Configuration',
        value: '12',
        component: ServiceConfigurationTab,
        Icon: Widgets,
      });
    }

    return nextTabs.sort((a, b) => a.value - b.value);
  }, [
    adsManagementFlagEnabled,
    showManagePrograms,
    showCourseReview,
    showFacilityLms,
    showProgramCertificates,
    showCapMapping,
    showServiceConfiguration,
  ]);

  return (
    <Box className={classes.folderWrapper}>
      <FolderNavComponent defaultActiveTab="1" color="var(--yellow-main)" tabs={tabs} />
    </Box>
  );
};

export default withUnleash(withStyles(styles)(SuperAdminModule), UnleashService.FLAGS.ADS_MANAGEMENT);
